import React from 'react';
import { NavLink } from 'react-router-dom';
import { cn } from '../lib/utils';
import { NAVY } from '../lib/theme';
import { Logo } from './Logo';
import {
  LayoutDashboard,
  Shield,
  ScrollText,
  Settings,
  PanelLeftClose,
  PanelLeftOpen,
  Activity,
  Users,
  ShieldCheck,
  PieChart
} from 'lucide-react';
import logoImage from '../assets/logo.png';
import { useAuth } from '../context/AuthContext';
import { useBranding } from '../context/BrandingContext';

const navigation = [
  { name: 'Beranda', href: '/dashboard', icon: LayoutDashboard, roles: ['admin', 'user'] },
  { name: 'Statistik & Kalender', href: '/statistics', icon: PieChart, roles: ['admin', 'user'] },
  { name: 'Sertifikat', href: '/certificates', icon: Shield, roles: ['admin', 'user'] },
  { name: 'Status Server', href: '/agents', icon: Activity, roles: ['admin'] },
  { name: 'Log Aktivitas', href: '/logs', icon: ScrollText, roles: ['admin'] },
  { name: 'Manajemen User', href: '/users', icon: Users, roles: ['admin'] },
  { name: 'FortiWeb Config', href: '/fortiweb', icon: ShieldCheck, roles: ['admin', 'user'] },
  { name: 'Pengaturan', href: '/settings', icon: Settings, roles: ['admin'] },
];

export function Sidebar({ collapsed, onToggle }) {
  const { user } = useAuth();
  const { branding } = useBranding();

  // Filter navigation based on user role
  const filteredNavigation = navigation.filter(item =>
    item.roles.includes(user?.role || 'user')
  );

  return (
    <div
      className={cn(
        "flex flex-col h-full transition-all duration-300 relative z-50 border-r",
        collapsed ? "w-16" : "w-64"
      )}
      style={{ background: NAVY.sidebar, borderColor: NAVY.sidebarBorder }}
    >
      {/* Logo Header */}
      <div
        className={cn(
          "flex items-center h-14 border-b transition-all duration-300",
          collapsed ? "justify-center px-0" : "px-4"
        )}
        style={{ borderColor: NAVY.sidebarBorder }}
      >
        {!collapsed ? (
          <div className="flex items-center gap-2.5 w-full">
            <div className="flex-shrink-0 bg-white/5 p-1 rounded-sm border border-white/10">
              <Logo
                src={branding.logo_url || logoImage}
                alt="Logo SSL Monitor"
                size="sm"
              />
            </div>
            <div className="flex flex-col min-w-0">
              <span
                className="font-semibold text-[13px] leading-tight truncate"
                style={{ color: NAVY.textActive }}
              >
                Monitoring SSL
              </span>
              <span
                className="text-[11px] font-medium truncate"
                style={{ color: NAVY.textMuted }}
              >
                {branding.header_text.replace('Monitoring SSL ', '').replace('Monitoring SSL', '') || 'Dashboard'}
              </span>
            </div>
          </div>
        ) : (
          <div className="mx-auto flex-shrink-0 bg-white/5 p-1 rounded-sm border border-white/10">
            <Logo
              src={branding.logo_url || logoImage}
              alt="Logo SSL Monitor"
              size="sm"
            />
          </div>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        {filteredNavigation.map((item) => (
          <NavLink
            key={item.name}
            to={item.href}
            className={({ isActive }) =>
              cn(
                "flex items-center gap-3 px-3 py-2 rounded-sm text-xs font-medium transition-colors",
                isActive
                  ? "bg-primary text-primary-foreground"
                  : "hover:bg-white/5",
                collapsed && "justify-center"
              )
            }
            style={({ isActive }) => (isActive ? undefined : { color: NAVY.textInactive })}
            title={collapsed ? item.name : undefined}
          >
            <item.icon className="h-4 w-4 flex-shrink-0" />
            {!collapsed && <span>{item.name}</span>}
          </NavLink>
        ))}
      </nav>

      {/* Edge Toggle Button */}
      <div className="absolute -right-3 top-4 z-50">
        <button
          type="button"
          onClick={onToggle}
          title={collapsed ? 'Perluas sidebar' : 'Ciutkan sidebar'}
          className="h-6 w-6 rounded-sm flex items-center justify-center transition-colors"
          style={{ background: NAVY.sidebar, border: `1px solid ${NAVY.sidebarBorder}`, color: NAVY.textInactive }}
          onMouseEnter={(e) => { e.currentTarget.style.color = NAVY.textActive; e.currentTarget.style.background = NAVY.dropdownBg; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = NAVY.textInactive; e.currentTarget.style.background = NAVY.sidebar; }}
        >
          {collapsed ? (
            <PanelLeftOpen className="h-3.5 w-3.5" />
          ) : (
            <PanelLeftClose className="h-3.5 w-3.5" />
          )}
        </button>
      </div>
    </div>
  );
}